import { z } from 'zod';
export { isBadgeTemplateV2 } from '../types/badge-template.js';
const positiveFinite = z.number().finite().positive();
const positiveInteger = positiveFinite.int();
const coordinate = z.number().finite();
const rotation = z.number().finite().min(-360).max(360);
const hexColor = z.string().regex(/^#[0-9a-fA-F]{6}$/);
const fontWeight = z.enum(['normal', 'bold']);
const fontStyle = z.enum(['normal', 'italic']);
const textAlign = z.enum(['left', 'center', 'right']);
const BadgeTextElementV1Schema = z.object({
    id: z.string(),
    type: z.literal('variable'),
    variable: z.string(),
    x: coordinate,
    y: coordinate,
    fontSize: positiveFinite,
    fontFamily: z.string(),
    fontWeight,
    fontStyle,
    color: hexColor,
    align: textAlign,
    maxWidth: positiveFinite.optional(),
    rotation: rotation.optional(),
});
const BadgeStaticTextElementV1Schema = z.object({
    id: z.string(),
    type: z.literal('static'),
    text: z.string(),
    x: coordinate,
    y: coordinate,
    fontSize: positiveFinite,
    fontFamily: z.string(),
    fontWeight,
    fontStyle,
    color: hexColor,
    align: textAlign,
    rotation: rotation.optional(),
});
const BadgePhotoElementV1Schema = z.object({
    id: z.string(),
    type: z.literal('photo'),
    x: coordinate,
    y: coordinate,
    width: positiveFinite,
    height: positiveFinite,
    shape: z.enum(['rect', 'circle']),
    borderWidth: z.number().finite().nonnegative().optional(),
    borderColor: hexColor.optional(),
    rotation: rotation.optional(),
});
const BadgeBarcodeElementV1Schema = z.object({
    id: z.string(),
    type: z.literal('barcode'),
    barcodeType: z.enum(['qr', 'barcode']),
    x: coordinate,
    y: coordinate,
    width: positiveFinite,
    height: positiveFinite,
    encodeTemplate: z.string(),
    rotation: rotation.optional(),
});
const BadgeElementV1Schema = z.discriminatedUnion('type', [
    BadgeTextElementV1Schema,
    BadgeStaticTextElementV1Schema,
    BadgePhotoElementV1Schema,
    BadgeBarcodeElementV1Schema,
]);
export const BadgeTemplateConfigV1Schema = z.object({
    version: z.literal(1).optional(),
    type: z.literal('PDF_OVERLAY'),
    background: z.object({
        storageKey: z.string(),
        publicUrl: z.string(),
        width: positiveFinite,
        height: positiveFinite,
    }),
    elements: z.array(BadgeElementV1Schema),
});
export const BadgeTextStyleV2Schema = z.object({
    fontFamily: z.string(),
    fontWeight,
    fontStyle,
    fontSizePt: positiveFinite,
    color: hexColor,
    align: textAlign,
    maxWidthPt: positiveFinite.optional(),
    overflow: z.enum(['clip', 'shrink-to-fit']).optional(),
    letterSpacingPt: coordinate.optional(),
});
const BadgeElementBaseV2Schema = {
    id: z.string(),
    xPt: coordinate,
    yPt: coordinate,
    rotationDeg: rotation.optional(),
};
const BadgeTextElementV2Schema = z.object({
    ...BadgeElementBaseV2Schema,
    type: z.literal('variable'),
    variable: z.string(),
    style: BadgeTextStyleV2Schema,
});
const BadgeStaticTextElementV2Schema = z.object({
    ...BadgeElementBaseV2Schema,
    type: z.literal('static'),
    text: z.string(),
    style: BadgeTextStyleV2Schema,
});
const BadgePhotoElementV2Schema = z.object({
    ...BadgeElementBaseV2Schema,
    type: z.literal('photo'),
    widthPt: positiveFinite,
    heightPt: positiveFinite,
    shape: z.enum(['rect', 'circle']),
    borderWidthPt: z.number().finite().nonnegative().optional(),
    borderColor: hexColor.optional(),
});
const BadgeBarcodeElementV2Schema = z.object({
    ...BadgeElementBaseV2Schema,
    type: z.literal('barcode'),
    barcodeType: z.enum(['qr', 'barcode']),
    widthPt: positiveFinite,
    heightPt: positiveFinite,
    encodeTemplate: z.string(),
});
const BadgeElementV2Schema = z.discriminatedUnion('type', [
    BadgeTextElementV2Schema,
    BadgeStaticTextElementV2Schema,
    BadgePhotoElementV2Schema,
    BadgeBarcodeElementV2Schema,
]);
const backgroundBase = {
    storageKey: z.string(),
    publicUrl: z.string(),
    pageWidthPt: positiveFinite,
    pageHeightPt: positiveFinite,
};
const BadgePdfBackgroundV2Schema = z.object({
    ...backgroundBase,
    mediaType: z.literal('application/pdf'),
    sizingSource: z.literal('pdf-page'),
}).strict();
const imageBackgroundBase = {
    ...backgroundBase,
    mediaType: z.enum(['image/jpeg', 'image/png']),
};
const BadgeImageDpiBackgroundV2Schema = z.object({
    ...imageBackgroundBase,
    sourcePixelWidth: positiveInteger,
    sourcePixelHeight: positiveInteger,
    sourceDpi: positiveFinite,
    sizingSource: z.literal('image-dpi'),
}).strict();
const BadgePresetImageBackgroundWithPixelsV2Schema = z.object({
    ...imageBackgroundBase,
    sourcePixelWidth: positiveInteger,
    sourcePixelHeight: positiveInteger,
    sourceDpi: positiveFinite.optional(),
    sizingSource: z.enum(['paper-preset', 'legacy-96dpi']),
}).strict();
const BadgePresetImageBackgroundWithoutPixelsV2Schema = z.object({
    ...imageBackgroundBase,
    sourceDpi: positiveFinite.optional(),
    sizingSource: z.enum(['paper-preset', 'legacy-96dpi']),
}).strict();
const BadgeBackgroundV2Schema = z.union([
    BadgePdfBackgroundV2Schema,
    BadgeImageDpiBackgroundV2Schema,
    BadgePresetImageBackgroundWithPixelsV2Schema,
    BadgePresetImageBackgroundWithoutPixelsV2Schema,
]);
export const BadgeTemplateConfigV2Schema = z.object({
    version: z.literal(2),
    type: z.literal('PDF_OVERLAY'),
    background: BadgeBackgroundV2Schema,
    elements: z.array(BadgeElementV2Schema),
});
export const BadgeTemplateConfigSchema = z.union([
    BadgeTemplateConfigV1Schema,
    BadgeTemplateConfigV2Schema,
]);
//# sourceMappingURL=badge-template.js.map